// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SuccessResponse } from '@/api/schemas';
import { AssetAssignment, assignAssets } from '@/project/api/assign-assets';
import {
  MutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';

interface Variables {
  projectKey: string;
  assignments: AssetAssignment[];
}

interface AssignmentInput {
  id: string;
  dateTime: DateTime;
}

type Options = Pick<
  MutationOptions<SuccessResponse, AxiosError, Variables>,
  'onSuccess' | 'onError'
>;

/**
 * A React hook that manages the assignment of assets to a project.
 *
 * @param options.onSuccess - A callback fired when the mutation is successful.
 */
export function useAssetAssignment(options?: Options) {
  const queryClient = useQueryClient();

  const { mutate, ...mutation } = useMutation({
    mutationFn: (parameters: Variables) => {
      return assignAssets(parameters.projectKey, parameters.assignments);
    },
    onSuccess: async (data, variables, context) => {
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }

      if (data.success) {
        await queryClient.invalidateQueries({ queryKey: ['asset'] });
        await queryClient.invalidateQueries({
          queryKey: ['project', variables.projectKey],
        });
      }
    },
    onError: options?.onError,
  });

  const assign = (projectKey: string, input: AssignmentInput[]) => {
    const assignments: AssetAssignment[] = input.map((assignment) => ({
      assetId: assignment.id,
      date: assignment.dateTime,
    }));

    mutate({ projectKey, assignments });
  };

  return { assign, ...mutation };
}
